import { apiService } from 'core/services/apiService';

export interface DataRetentionSetting {
	'@id'?: string;
	'@type'?: string;
	id: number;
	module: string;
	label: string;
	retentionPeriod: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface UpdateDataRetentionSettingPayload {
	module: string;
	label: string;
	retentionPeriod: string;
}

export const RETENTION_PERIOD_OPTIONS = [
	'1 Week',
	'2 Weeks',
	'3 Weeks',
	'1 Month',
	'3 Months',
	'6 Months',
	'1 Year',
	'2 Years',
	'5 Years',
	'10 Years',
] as const;

export type RetentionPeriodOption = (typeof RETENTION_PERIOD_OPTIONS)[number];

export const parseDataRetentionSettings = (data: unknown): DataRetentionSetting[] => {
	if (Array.isArray(data)) {
		return data as DataRetentionSetting[];
	}

	if (data && typeof data === 'object') {
		const record = data as Record<string, unknown>;
		if (Array.isArray(record.member)) {
			return record.member as DataRetentionSetting[];
		}
		if (Array.isArray(record['hydra:member'])) {
			return record['hydra:member'] as DataRetentionSetting[];
		}
		if (Array.isArray(record.items)) {
			return record.items as DataRetentionSetting[];
		}
	}

	return [];
};

export const getRetentionPeriodSelectOptions = (currentValue?: string): string[] => {
	const options = [...RETENTION_PERIOD_OPTIONS];
	if (currentValue && !options.includes(currentValue as RetentionPeriodOption)) {
		return [currentValue, ...options];
	}
	return options;
};

export const getChangedDataRetentionSettings = (
	original: DataRetentionSetting[],
	draft: DataRetentionSetting[]
): DataRetentionSetting[] => {
	const originalById = new Map(original.map((setting) => [setting.id, setting]));
	return draft.filter((setting) => originalById.get(setting.id)?.retentionPeriod !== setting.retentionPeriod);
};

export const buildDataRetentionPatchPayload = (
	setting: DataRetentionSetting
): UpdateDataRetentionSettingPayload => ({
	module: setting.module,
	label: setting.label,
	retentionPeriod: setting.retentionPeriod,
});

export const getDataRetentionSettings = async (): Promise<DataRetentionSetting[]> => {
	try {
		const response = await apiService.get('data_retention_settings?pagination=false');
		const data = apiService.responseHandler(response);
		return parseDataRetentionSettings(data);
	} catch (error: any) {
		console.error('Failed to fetch data retention settings:', error);
		throw new Error(error?.message || 'Failed to fetch data retention settings');
	}
};

export const updateDataRetentionSetting = async (
	id: number,
	payload: UpdateDataRetentionSettingPayload
): Promise<DataRetentionSetting> => {
	const response = await apiService.patch(`data_retention_settings/${id}`, payload);
	return apiService.responseHandler(response);
};

export const saveChangedDataRetentionSettings = async (
	original: DataRetentionSetting[],
	draft: DataRetentionSetting[]
): Promise<void> => {
	const changed = getChangedDataRetentionSettings(original, draft);
	await Promise.all(
		changed.map((setting) =>
			updateDataRetentionSetting(setting.id, buildDataRetentionPatchPayload(setting))
		)
	);
};
